import { AuthContext } from "context/AuthContext";
import { fetchWithParams } from "lib/fetcher";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import useSWR, { useSWRConfig } from "swr";
import SiteItem from "./site-item";
import { CameraInfo, WsResDataType } from "lib/types";
import { SvgClose, SvgArrowRight, SvgBack, SvgToastInfo } from "ui/svgs";
import clsx from "clsx";
import { Select, Modal, Input, Progress } from "antd";
import type { WsCameraSettingReq } from "pages/dashboard/customers/service";
import "./table.scss";
import { useCustomersService } from "pages/dashboard/customers/service";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useNavigate, useLocation } from "react-router-dom";
import PubSub from "pubsub-js";

export default function SitesTable({ orgId }: { orgId: string }) {
  const navigate = useNavigate();

  const { pathname } = useLocation();
  const isEncoding = pathname.includes("encoding");

  const { user } = useContext(AuthContext);
  const [selectedCameras, setSelectedCameras] = useState<CameraInfo[]>([]);
  const [saving, setSaving] = useState(false);
  const [isSettingResModalOpen, setIsSettingResModalOpen] = useState(false);
  const [siteQuery, setSiteQuery] = useState("");
  const [results, setResults] = useState([]);
  const [progress, setProgress] = useState([0, 1]);

  const [hdResolution, setHdResolution] = useState<{
    height: number;
    width: number;
    name: string;
  }>();

  const [hdFrameRate, setHdFrameRate] = useState<number>();
  const [hdBitRate, setHdBitRate] = useState<number>();
  const [sdResolution, setSdResolution] = useState<{
    height: number;
    width: number;
    name: string;
  }>();
  const [sdFrameRate, setSdFrameRate] = useState<number>();
  const [sdBitRate, setSdBitRate] = useState<number>();

  const { sendUpdateCameraSettingRequest } = useCustomersService();

  const { data, error, isLoading } = useSWR(
    [`/site/tree?withCameraNum=true&withMonitorInfo=true`, user.token, orgId],
    ([url, token, orgId]) => fetchWithParams(url, token, orgId),
    {
      revalidateOnFocus: true,
    },
  );

  useEffect(() => {
    const handleCamerasSelectChange = (
      _,
      props: { siteId: number; cameras: CameraInfo[] },
    ) => {
      const { siteId, cameras } = props;
      console.log("selectedCameras", selectedCameras);
      const reservedCameras = selectedCameras?.filter(
        (camera) => camera.siteId !== siteId,
      );
      setSelectedCameras([...reservedCameras, ...cameras]);
    };
    const subscription = PubSub.subscribe(
      "encodeCamerasSelectedChanged",
      handleCamerasSelectChange,
    );
    return () => {
      PubSub.unsubscribe("encodeCamerasSelectedChanged", subscription);
    };
  }, [selectedCameras]);

  useEffect(() => {
    setSelectedCameras([]);
    // setSiteQuery("");
  }, [isEncoding]);

  const filterSites = useMemo(() => {
    const filterSite = data?.sites?.filter((site: any) =>
      site?.name?.toUpperCase().includes(siteQuery?.toUpperCase()),
    );
    return filterSite || [];
  }, [data, siteQuery]);

  useEffect(() => {
    const filterCameras = selectedCameras?.filter((camera) => {
      return filterSites?.find(
        (filterSite) => filterSite?.id == camera?.siteId,
      );
    });
    setSelectedCameras(filterCameras);
  }, [filterSites]);

  // const hdResolutionOptions = [];
  // const hdFrameRateOptions = [];
  // const hdBitRateOptions = [];
  // const sdResolutionOptions = [];
  // const sdFrameRateOptions = [];
  // const sdBitRateOptions = [];

  const hdResolutionOptions = useMemo(() => {
    if (
      !selectedCameras.length ||
      selectedCameras.some(
        (arr) => arr.capabilities?.mainStream?.resolutions?.length === 0,
      )
    ) {
      return [];
    }

    // Create a set from the objects of the first array (based on the specified key)
    const namesKey = new Set(
      selectedCameras[0]?.capabilities?.mainStream?.resolutions?.map(
        (resolution) => resolution.name,
      ),
    );

    // Iterate over the other arrays in the 2D array
    for (let i = 1; i < selectedCameras.length; i++) {
      // Set of objects from the current array (based on the specified key)
      const currentKeys = new Set(
        selectedCameras[i]?.capabilities?.mainStream?.resolutions?.map(
          (resolution) => resolution.name,
        ),
      );
      // Calculate the intersection with the current set
      for (const keyValue of namesKey) {
        if (!currentKeys.has(keyValue)) {
          namesKey.delete(keyValue);
        }
      }
    }

    // Find the objects from the first array that appear in all arrays
    return (
      selectedCameras[0]?.capabilities?.mainStream?.resolutions?.filter(
        (resolution) => namesKey.has(resolution?.name),
      ) || []
    );
  }, [selectedCameras]);

  const hdFrameRateOptions = useMemo(() => {
    // Create a set from the objects of the first array (based on the specified key)
    const namesKey = new Set(
      selectedCameras[0]?.capabilities?.mainStream?.resolutions?.find(
        (reso) => reso?.name === hdResolution?.name,
      )?.frameRates,
    );

    // Iterate over the other arrays in the 2D array
    for (let i = 1; i < selectedCameras.length; i++) {
      // Set of objects from the current array (based on the specified key)
      const currentKeys = new Set(
        selectedCameras[i]?.capabilities?.mainStream?.resolutions?.find(
          (reso) => reso?.name === hdResolution?.name,
        )?.frameRates,
      );
      // Calculate the intersection with the current set
      for (const keyValue of namesKey) {
        if (!currentKeys.has(keyValue)) {
          namesKey.delete(keyValue);
        }
      }
    }

    // Find the objects from the first array that appear in all arrays
    return (
      selectedCameras[0]?.capabilities?.mainStream?.resolutions
        ?.find((reso) => reso?.name === hdResolution?.name)
        ?.frameRates?.filter((frameRate) => namesKey.has(frameRate)) || []
    );
  }, [selectedCameras, hdResolution]);

  const hdBitRateOptions = useMemo(() => {
    // Create a set from the objects of the first array (based on the specified key)
    const namesKey = new Set(
      selectedCameras[0]?.capabilities?.mainStream?.resolutions?.find(
        (reso) => reso?.name === hdResolution?.name,
      )?.bitRates,
    );

    // Iterate over the other arrays in the 2D array
    for (let i = 1; i < selectedCameras.length; i++) {
      // Set of objects from the current array (based on the specified key)
      const currentKeys = new Set(
        selectedCameras[i]?.capabilities?.mainStream?.resolutions?.find(
          (reso) => reso?.name === hdResolution?.name,
        )?.bitRates,
      );
      // Calculate the intersection with the current set
      for (const keyValue of namesKey) {
        if (!currentKeys.has(keyValue)) {
          namesKey.delete(keyValue);
        }
      }
    }

    // Find the objects from the first array that appear in all arrays
    return (
      selectedCameras[0]?.capabilities?.mainStream?.resolutions
        ?.find((reso) => reso?.name === hdResolution?.name)
        ?.bitRates?.filter((bitRate) => namesKey.has(bitRate)) || []
    );
  }, [selectedCameras, hdResolution]);

  const sdResolutionOptions = useMemo(() => {
    if (
      !selectedCameras.length ||
      selectedCameras.some(
        (arr) => arr.capabilities?.subStream?.resolutions?.length === 0,
      )
    ) {
      return [];
    }

    // Create a set from the objects of the first array (based on the specified key)
    const namesKey = new Set(
      selectedCameras[0]?.capabilities?.subStream?.resolutions?.map(
        (resolution) => resolution.name,
      ),
    );

    // Iterate over the other arrays in the 2D array
    for (let i = 1; i < selectedCameras.length; i++) {
      // Set of objects from the current array (based on the specified key)
      const currentKeys = new Set(
        selectedCameras[i]?.capabilities?.subStream?.resolutions?.map(
          (resolution) => resolution.name,
        ),
      );
      // Calculate the intersection with the current set
      for (const keyValue of namesKey) {
        if (!currentKeys.has(keyValue)) {
          namesKey.delete(keyValue);
        }
      }
    }

    // Find the objects from the first array that appear in all arrays
    return (
      selectedCameras[0]?.capabilities?.subStream?.resolutions?.filter(
        (resolution) => namesKey.has(resolution?.name),
      ) || []
    );
  }, [selectedCameras]);

  const sdFrameRateOptions = useMemo(() => {
    // Create a set from the objects of the first array (based on the specified key)
    const namesKey = new Set(
      selectedCameras[0]?.capabilities?.subStream?.resolutions?.find(
        (reso) => reso?.name === sdResolution?.name,
      )?.frameRates,
    );

    // Iterate over the other arrays in the 2D array
    for (let i = 1; i < selectedCameras.length; i++) {
      // Set of objects from the current array (based on the specified key)
      const currentKeys = new Set(
        selectedCameras[i]?.capabilities?.subStream?.resolutions?.find(
          (reso) => reso?.name === sdResolution?.name,
        )?.frameRates,
      );
      // Calculate the intersection with the current set
      for (const keyValue of namesKey) {
        if (!currentKeys.has(keyValue)) {
          namesKey.delete(keyValue);
        }
      }
    }

    // Find the objects from the first array that appear in all arrays
    return (
      selectedCameras[0]?.capabilities?.subStream?.resolutions
        ?.find((reso) => reso?.name === sdResolution?.name)
        ?.frameRates?.filter((frameRate) => namesKey.has(frameRate)) || []
    );
  }, [selectedCameras, sdResolution]);

  const sdBitRateOptions = useMemo(() => {
    // Create a set from the objects of the first array (based on the specified key)
    const namesKey = new Set(
      selectedCameras[0]?.capabilities?.subStream?.resolutions?.find(
        (reso) => reso?.name === sdResolution?.name,
      )?.bitRates,
    );

    // Iterate over the other arrays in the 2D array
    for (let i = 1; i < selectedCameras.length; i++) {
      // Set of objects from the current array (based on the specified key)
      const currentKeys = new Set(
        selectedCameras[i]?.capabilities?.subStream?.resolutions?.find(
          (reso) => reso?.name === sdResolution?.name,
        )?.bitRates,
      );
      // Calculate the intersection with the current set
      for (const keyValue of namesKey) {
        if (!currentKeys.has(keyValue)) {
          namesKey.delete(keyValue);
        }
      }
    }

    // Find the objects from the first array that appear in all arrays
    return (
      selectedCameras[0]?.capabilities?.subStream?.resolutions
        ?.find((reso) => reso?.name === sdResolution?.name)
        ?.bitRates?.filter((bitRate) => namesKey.has(bitRate)) || []
    );
  }, [selectedCameras, sdResolution]);

  useEffect(() => {
    setHdResolution(hdResolutionOptions?.at(0));
  }, [hdResolutionOptions]);

  useEffect(() => {
    setHdFrameRate(hdFrameRateOptions?.at(0));
    setHdBitRate(hdBitRateOptions?.at(0));
  }, [hdResolution]);

  useEffect(() => {
    setSdResolution(sdResolutionOptions?.at(0));
  }, [sdResolutionOptions]);

  useEffect(() => {
    setSdFrameRate(sdFrameRateOptions?.at(0));
    setSdBitRate(sdBitRateOptions?.at(0));
  }, [sdResolution]);

  const groupByNvr = (cameras: CameraInfo[]) => {
    const nvrMap = new Map<number, CameraInfo[]>();
    cameras.forEach((camera) => {
      if (nvrMap.has(camera.nvrId)) {
        nvrMap.get(camera.nvrId).push(camera);
      } else {
        nvrMap.set(camera.nvrId, [camera]);
      }
    });
    let maxNvrCameras = 0;
    const groups: CameraInfo[][] = [];
    nvrMap?.forEach((value) => {
      groups.push(value);
      maxNvrCameras = Math.max(maxNvrCameras, value.length);
    });
    const senderCameras: CameraInfo[][] = [];
    for (let j = 0; j < maxNvrCameras; j++) {
      for (let i = 0; i < groups.length; i++) {
        const curNvrCameras = groups[i];

        if (curNvrCameras?.length > j) {
          senderCameras[j] = senderCameras[j] || [];
          senderCameras[j].push(curNvrCameras[j]);
        }
      }
    }
    console.log("senderCameras", senderCameras);
    return senderCameras;
  };

  const handleSave = async (cameras: CameraInfo[]) => {
    setSaving(true);
    setIsSettingResModalOpen(true);
    const senderCameras = groupByNvr(cameras);

    for (let i = 0; i < senderCameras.length; i++) {
      const camerasReq = senderCameras[i]?.map((camera) => {
        const cameraSetting: WsCameraSettingReq = {
          id: +camera.id,
          name: camera.name,
          mainStream: {
            resolution: hdResolution,
            frameRate: hdFrameRate,
            bitRate: hdBitRate,
          },
          subStream: {
            resolution: sdResolution,
            frameRate: sdFrameRate,
            bitRate: sdBitRate,
          },
        };
        return cameraSetting;
      });

      console.log("lzc cameras = ", JSON.stringify(camerasReq));

      try {
        const res = await sendUpdateCameraSettingRequest(camerasReq, orgId);

        const data = (res as WsResDataType)?.deviceUpdateCamerasSettingsReply
          ?.data;
        setProgress((pre) => [pre[0] + data?.length, pre[1]]);
        setResults((pres) => {
          const filter = pres?.filter(
            (old) =>
              !data?.find(
                (newD) =>
                  newD?.streamSettingsRes?.id == old?.streamSettingsRes?.id,
              ),
          );
          return [...filter, ...data];
        });
        console.log("handleSave", res);
      } catch (error) {
        console.error(error);
      }
    }

    setSaving(false);
  };

  const resetBatchEncodeCameras = () => {
    setSelectedCameras([]);
    PubSub.publish("resetBatchEncodeCameras");
  };

  //if (isLoading) return <div>Loading...</div>;
  // if (!data) {
  //   throw new Error(error);
  // }

  const errorResults = results?.filter(
    (result) => result?.streamSettingsRes?.error,
  );
  const succsssCount = results?.length - errorResults?.length;

  const isOneofOptionDisabled =
    hdResolutionOptions.length === 0 ||
    hdFrameRateOptions.length === 0 ||
    hdBitRateOptions.length === 0 ||
    sdResolutionOptions.length === 0 ||
    sdFrameRateOptions.length === 0 ||
    sdBitRateOptions.length === 0;

  return (
    <div className="site-table pt-2 pb-3">
      <div className="flex items-center mb-3">
        {isEncoding && (
          <button
            className="w-12 h-10  flex items-center justify-center mr-6 "
            onClick={() => {
              navigate(`/dashboard/customers/${orgId}/sites`);
              setSelectedCameras([]);
            }}
          >
            <SvgBack width="16" className={"fill-zinc-500"} />
          </button>
        )}

        <Input
          placeholder="Search site name"
          className="h-10 w-64 rounded-2xl"
          onChange={(e) => {
            setSiteQuery(e.target.value);
          }}
          prefix={
            <MagnifyingGlassIcon className="w-4 text-gray-500 peer-focus:text-gray-900" />
          }
        />
      </div>

      {isEncoding && selectedCameras?.length > 0 && (
        <div className="bg-[#F5F5F5] rounded-2xl py-4 px-4 mb-4">
          <div className="flex items-center gap-3">
            <button onClick={resetBatchEncodeCameras}>
              <SvgClose width="16" className="fill-[#222222] opacity-25" />
            </button>
            {selectedCameras.length}&nbsp;Selected
            {selectedCameras.length > 100 && (
              <div className="flex gap-1">
                <SvgToastInfo width="14" className="fill-[#F73D2F]" />
                <p className="text-[#F73D2F]">Maximum 100 cameras</p>
              </div>
            )}
          </div>
          <div className="flex justify-between pl-6  mt-4">
            <div className="">
              <div className="flex items-center">
                <p className="font-semibold mr-3 w-8">HD:</p>
                <p className="mr-4 text-nowrap">Resolution</p>
                <Select
                  disabled={hdResolutionOptions?.length === 0}
                  className="batch-select"
                  popupClassName="site-table-select-dropdown"
                  style={{ width: "100%", borderRadius: "16px" }}
                  dropdownStyle={{ borderRadius: "16px", padding: "4px 0" }}
                  suffixIcon={
                    <SvgArrowRight
                      className={clsx("rotate-90 fill-neutral-500")}
                      width="16"
                    />
                  }
                  value={hdResolution?.name}
                  onChange={(value) => {
                    console.log("default onchanged", value);
                    setHdResolution(
                      hdResolutionOptions?.find(
                        (resolution) => resolution.name === value,
                      ),
                    );

                    console.log("hd resolution value = ", value);
                  }}
                  options={hdResolutionOptions?.map((resolution) => {
                    return {
                      value: resolution.name,
                      label: resolution.name,
                    };
                  })}
                />
                <p className="mx-4 text-nowrap">Frame Rate</p>
                <Select
                  disabled={hdFrameRateOptions?.length === 0}
                  className="batch-select"
                  popupClassName="site-table-select-dropdown"
                  style={{ width: "100%", borderRadius: "16px" }}
                  dropdownStyle={{ borderRadius: "16px", padding: "4px 0" }}
                  suffixIcon={
                    <SvgArrowRight
                      className={clsx("rotate-90 fill-neutral-500")}
                      width="16"
                    />
                  }
                  value={hdFrameRate}
                  onChange={(value) => {
                    setHdFrameRate(value);
                    console.log("hd frame rate value = ", value);
                  }}
                  options={hdFrameRateOptions?.map((frameRate) => {
                    return {
                      value: frameRate,
                      label: frameRate,
                    };
                  })}
                />
                <p className="mx-4 text-nowrap">Bit Rate</p>
                <Select
                  disabled={hdBitRateOptions?.length === 0}
                  className="batch-select"
                  popupClassName="site-table-select-dropdown"
                  style={{ width: "100%", borderRadius: "16px" }}
                  dropdownStyle={{ borderRadius: "16px", padding: "4px 0" }}
                  suffixIcon={
                    <SvgArrowRight
                      className={clsx("rotate-90 fill-neutral-500")}
                      width="16"
                    />
                  }
                  value={hdBitRate}
                  onChange={(value) => {
                    setHdBitRate(value);
                    console.log("hd bit rate value = ", value);
                  }}
                  options={hdBitRateOptions?.map((bitRate) => {
                    return {
                      value: bitRate,
                      label: bitRate,
                    };
                  })}
                />
              </div>
              <div className="flex items-center">
                <p className="font-semibold mr-3 w-8">SD:</p>
                <p className="mr-4 text-nowrap">Resolution</p>
                <Select
                  disabled={sdResolutionOptions?.length === 0}
                  className="batch-select"
                  popupClassName="site-table-select-dropdown"
                  style={{ width: "100%", borderRadius: "16px" }}
                  dropdownStyle={{ borderRadius: "16px", padding: "4px 0" }}
                  suffixIcon={
                    <SvgArrowRight
                      className={clsx("rotate-90 fill-neutral-500")}
                      width="16"
                    />
                  }
                  value={sdResolution?.name}
                  onChange={(value) => {
                    setSdResolution(
                      sdResolutionOptions?.find(
                        (resolution) => resolution.name === value,
                      ),
                    );
                    console.log("sd resolution value = ", value);
                  }}
                  options={sdResolutionOptions?.map((resolution) => {
                    return {
                      value: resolution.name,
                      label: resolution.name,
                    };
                  })}
                />
                <p className="mx-4 text-nowrap">Frame Rate</p>
                <Select
                  disabled={sdFrameRateOptions?.length === 0}
                  className="batch-select"
                  popupClassName="site-table-select-dropdown"
                  style={{ width: "100%", borderRadius: "16px" }}
                  dropdownStyle={{ borderRadius: "16px", padding: "4px 0" }}
                  suffixIcon={
                    <SvgArrowRight
                      className={clsx("rotate-90 fill-neutral-500")}
                      width="16"
                    />
                  }
                  value={sdFrameRate}
                  onChange={(value) => {
                    setSdFrameRate(value);
                    console.log("sd frame rate value = ", value);
                  }}
                  options={sdFrameRateOptions?.map((frameRate) => {
                    return {
                      value: frameRate,
                      label: frameRate,
                    };
                  })}
                />
                <p className="mx-4 text-nowrap">Bit Rate</p>
                <Select
                  disabled={sdBitRateOptions?.length === 0}
                  className="batch-select"
                  popupClassName="site-table-select-dropdown"
                  style={{ width: "100%", borderRadius: "16px" }}
                  dropdownStyle={{ borderRadius: "16px", padding: "4px 0" }}
                  suffixIcon={
                    <SvgArrowRight
                      className={clsx("rotate-90 fill-neutral-500")}
                      width="16"
                    />
                  }
                  value={sdBitRate}
                  onChange={(value) => {
                    setSdBitRate(value);
                    console.log("sd bit rate value = ", value);
                  }}
                  options={sdBitRateOptions?.map((bitRate) => {
                    return {
                      value: bitRate,
                      label: bitRate,
                    };
                  })}
                />
              </div>
            </div>
            <button
              disabled={
                saving || isOneofOptionDisabled || selectedCameras?.length > 100
              }
              className={clsx(
                "mt-4 float-right flex justify-center w-[103px] h-[45px] items-center rounded-2xl bg-blue-500 px-6 text-base font-medium text-white transition-colors hover:bg-blue-400 disabled:bg-opacity-30 disabled:hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500",
              )}
              onClick={() => {
                setResults([]);
                setProgress([0, selectedCameras?.length]);
                handleSave(selectedCameras);
              }}
            >
              {saving ? (
                <div
                  className="ml-1 text-blue-500  inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                  role="status"
                ></div>
              ) : (
                "Confirm"
              )}
            </button>
          </div>
        </div>
      )}
      {filterSites.map((data: any) => (
        <SiteItem
          objData={data}
          key={data.id}
          //handleCamerasSelectChange={handleCamerasSelectChange}
        />
      ))}
      {saving && (
        <div className="w-full h-full absolute top-0 left-0 bg-zinc-400 bg-opacity-20 flex items-center justify-center">
          <div
            className="ml-1 text-blue-500  inline-block h-12 w-12 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
            role="status"
          ></div>
        </div>
      )}

      <Modal
        className="setting-results-modal"
        title={<p className="text-lg font-semibold">Setting Results</p>}
        open={isSettingResModalOpen}
        closable={false}
        centered
        footer={
          <div className="mt-6 mb-2">
            <button
              className="rounded-2xl bg-white px-6 py-4 border border-blue-500 hover:border-blue-400 text-sm  transition-colors text-blue-500 hover:text-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
              onClick={() => {
                !saving && window.location.reload();
                setIsSettingResModalOpen(false);
              }}
            >
              Close
            </button>
            {!saving && errorResults?.length > 0 && (
              <button
                className="ml-4 rounded-2xl px-6 py-4 bg-blue-500 text-sm font-medium text-white transition-colors hover:bg-blue-400 disabled:hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 disabled:opacity-50"
                onClick={async () => {
                  const errCameras = selectedCameras?.filter((camera) =>
                    errorResults?.find(
                      (error) => error?.streamSettingsRes?.id === camera?.id,
                    ),
                  );
                  setResults((pres) =>
                    pres?.filter((result) => !result?.streamSettingsRes?.error),
                  );
                  setProgress([0, errCameras?.length]);
                  handleSave(errCameras);
                }}
              >
                Retry Failed
              </button>
            )}
          </div>
        }
      >
        <div className="mt-6 flex justify-between items-center">
          <p className="font-semibold text-base">
            {saving ? "In Progress..." : "Complete"}
          </p>
          <p className="text-neutral-800 text-base">
            {progress?.at(0)}&nbsp; of&nbsp; {progress?.at(1)}&nbsp; Cameras
            Processed
          </p>
        </div>
        <Progress
          percent={(progress?.at(0) / progress?.at(1)) * 100}
          showInfo={false}
          strokeColor={
            !saving && errorResults?.length == 0 ? "#52c41a" : "#3E83F6"
          }
        />
        <p className="font-semibold mt-6 text-base">
          Successfully Updated Cameras: {succsssCount}
        </p>
        <p className="font-semibold mt-6 text-base">
          Failed to Update Cameras: {errorResults?.length}
        </p>
        {errorResults?.map((result) => (
          <p key={result?.id} className="ml-2 text-base">
            • &nbsp;
            {
              selectedCameras?.find(
                (camera) => camera?.id === result?.streamSettingsRes?.id,
              )?.name
            }
            {result?.error?.name}
          </p>
        ))}
      </Modal>
    </div>
  );
}
